import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostBinding,
  Inject,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import {
  AiFeaturesService,
  AskAiInput,
  GenerateIqBoostRequest,
  IQBoostCreateCRMAction,
  IQBOOST_V2_FEATURE_FLAG,
  IQBoostViewStore,
  IqBoostPromptCategory,
  isGlobalServersInEffect,
  LanguageHelper,
  MaximizerAiService,
  ViewType,
  PiiAnonymizationService,
} from '@maximizer/ai/data-access';
import {
  AddressBookService,
  AppointmentService,
  ContextService,
  FeatureService,
  NoteService,
  OpportunityService,
  SessionService,
  TaskService,
} from '@maximizer/core/shared/data-access';
import {
  CDN_URL,
  EntryType,
  Octopus,
  OpenAI,
} from '@maximizer/core/shared/domain';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import {
  NotificationComponent,
  UI_NOTIFICATION,
} from '@maximizer/core/shared/ui/components/notification';
import {
  ActionEventType,
  OpenEntityArgs,
} from '@maximizer/core/shared/ui/config';
import { MenuOptionName } from '@maximizer/core/shared/ui/enums';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import {
  ResourceOrTranslatePipe,
  UI_PIPES,
} from '@maximizer/core/shared/ui/pipes';
import { TimelineService } from '@maximizer/core/timeline/data-access';
import {
  EmailMessageRequest,
  EmailService,
} from '@maximizer/email-compose/data-access';
import { isNullOrEmpty } from '@maximizer/shared/util';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { KENDO_TEXTAREA } from '@progress/kendo-angular-inputs';
import {
  animationFrameScheduler,
  auditTime,
  catchError,
  exhaustMap,
  filter,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { v4 as uuidV4 } from 'uuid';
import {
  EventAction,
  EventJourney,
  IQBoostEventTracker,
} from '../classes/event-tracker';
import { IQBoostDocumentPreviewService } from '../services/iqboost-document-preview.service';
import { AI_GENERATED_CATEGORY, Helper, IQBoostArgs } from '../classes/iqboost';
import { IqboostChatComponent } from '../iqboost-chat/iqboost-chat.component';
import { IqboostPrivacySecurityCenterComponent } from '../iqboost-privacy-security-center/iqboost-privacy-security-center.component';
import { IqboostReportComponent } from '../iqboost-report/iqboost-report.component';
import { IqboostSummaryComponent } from '../iqboost-summary/iqboost-summary.component';
import { DeAnonymizerPipe } from '../pipes/de-anonymizer.pipe';
import { MarkdownRenderingPipe } from '../pipes/markdown-rendering.pipe';
import { SafeHtmlPipe } from '../pipes/safe-html.pipe';
import { AiMarkupUtils } from '../classes/ai-markup-utils';

@Component({
  selector: 'ai-iqboost-classic',
  templateUrl: './iqboost-classic.component.html',
  styleUrl: './iqboost-classic.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ResourceOrTranslatePipe, IQBoostDocumentPreviewService, IQBoostEventTracker],
  imports: [
    DeAnonymizerPipe,
    IqboostSummaryComponent,
    IqboostReportComponent,
    IqboostChatComponent,
    IqboostPrivacySecurityCenterComponent,
    KENDO_BUTTON,
    KENDO_DIALOG,
    KENDO_TEXTAREA,
    NgTemplateOutlet,
    NgClass,
    SafeHtmlPipe,
    TranslatePipe,
    UI_KENDO,
    UI_PIPES,
    UI_NOTIFICATION,
  ],
})
export class IqboostClassicComponent extends EmbeddedComponent {
  ViewType = ViewType;
  isNullOrEmpty = isNullOrEmpty;

  @HostBinding('class') hostClasses = 'ai-ng ai-headless-ng';
  aiClass = true;
  @ViewChild('baseNotifications')
  baseNotifications!: NotificationComponent;

  viewType = computed(() => this.store.viewType());
  entryAndOpportunityDisplay = computed(() => {
    const addressBookDisplayValue = this.store.entryDisplayValue();
    const opportunityDisplayValue = this.store.opportunityDisplayValue();
    let displayText = '';
    
    if (addressBookDisplayValue) {
      displayText += ` - ${addressBookDisplayValue}`;
    }
    if (opportunityDisplayValue) {
      displayText += ` (${opportunityDisplayValue})`;
    }
    return displayText;
  });

  confirmationDialogMessage = signal('');
  notificationMessage = signal('');
  isConfirmationDialogOpen = signal(false);
  firstLoad = signal(true);
  isOpen = signal(false);
  isSuggestPromptDialogOpen = signal(false);
  suggestPromptBody = signal('');
  fromModule = signal<{ key: string, type: EntryType } | undefined>(undefined);

  loadingGifUrl = computed(() => `${this.context.integration?.url ?? ''}/resources/ai-loading.gif`);
  /** Notes are rich text, so the markdown summary is rendered to HTML before it is saved. */
  private readonly markdown = new MarkdownRenderingPipe();
  anonymized = computed(() => this.piiAnonymization.anonymized());
  useGlobalServers = computed(() => {
    const group = this.store.dataProcessingFeatureGroup();
    return isGlobalServersInEffect(
      group.admin.dataProcessingFeatures,
      group.user.dataProcessingFeatures.useGlobalServers,
      this.feature.isFeatureOn(IQBOOST_V2_FEATURE_FLAG, false),
    );
  });
  useWebData = computed(() => {
    const group = this.store.dataProcessingFeatureGroup();
    return (
      group.user.dataProcessingFeatures.useWebData &&
      group.admin.dataProcessingFeatures.useWebData
    );
  });

  private readonly documentPreview = inject(IQBoostDocumentPreviewService);
  private readonly feature = inject(FeatureService);

  constructor(
    element: ElementRef,
    private readonly context: ContextService,
    private readonly session: SessionService,
    private readonly timeline: TimelineService,
    private readonly resourceOrTranslate: ResourceOrTranslatePipe,
    private readonly addressBook: AddressBookService,
    private readonly opportunity: OpportunityService,
    private readonly aiFeaturesService: AiFeaturesService,
    private readonly emailService: EmailService,
    private readonly appointment: AppointmentService,
    private readonly task: TaskService,
    private readonly note: NoteService,
    private readonly tracker: IQBoostEventTracker,
    public readonly store: IQBoostViewStore,
    private readonly piiAnonymization: PiiAnonymizationService,
    private readonly maximizerAi: MaximizerAiService,
    @Inject(CDN_URL) readonly cdn: string,
  ) {
    super(element);
  }

  private subscribeToEvents(): void {
    this.addSubscription(
      this.store
        .triggerAskAiSubject()
        .asObservable()
        .pipe(
          switchMap((input) =>
            this.askAi(input).pipe(
              takeUntil(this.store.cancelInFlightSubject()),
              finalize(() => this.store.setLoading(false)),
            ),
          ),
        )
        .subscribe(),
    );

    // Scope/privacy/household change: refresh the scoped prompt catalog, then re-run the current prompt.
    this.addSubscription(
      this.store
        .triggerScopeChangeSubject()
        .asObservable()
        .pipe(
          // The trigger cancels the in-flight request first, whose finalize clears loading.
          tap(() => this.store.setLoading(true)),
          tap((request) => this.store.setUseAllHouseholds(request.useAllHouseholds)),
          // The catalog is scope-specific; a stale one would ask with a prompt from the wrong set.
          switchMap(() => this.loadPromptCatalog()),
          tap(() => {
            if (this.store.currentPrompt()) {
              this.store.triggerAskAiEvent();
            } else {
              this.store.setLoading(false);
            }
          }),
        )
        .subscribe()
    );

    this.addSubscription(
      this.store
        .responseFromAiSubject()
        .asObservable()
        .pipe(
          tap((aiResponseText) => {
            if (this.store.viewType() === ViewType.Report) {
              this.store.setAiResponse(aiResponseText, this.store.viewType())
            }
          }),
        )
        .subscribe(),
    );

    this.addSubscription(
      this.store
        .triggerShowConfirmationDialogSubject()
        .asObservable()
        .subscribe((message) => {
          this.confirmationDialogMessage.set(message);
          this.isConfirmationDialogOpen.set(true);
        }),
    );

    // A cited PDF opens in the document viewer; the host opens anything else. Clicks while a
    // document's name is being read are dropped, so a double click opens one viewer.
    this.addSubscription(
      this.store
        .triggerOpenEntitySubject()
        .asObservable()
        .pipe(
          exhaustMap((entity) =>
            this.documentPreview.open(entity).pipe(
              filter((previewed) => !previewed),
              map(() => entity),
            ),
          ),
        )
        .subscribe((entity) => this.actionCompleted.emit(entity)),
    );

    this.addSubscription(
      this.store
        .triggerSendSuggestionSubject()
        .asObservable()
        .subscribe(() => {
          this.isSuggestPromptDialogOpen.set(true);
          this.suggestPromptBody.set('');
        }),
    );
  }

  override doAction(
    name: string,
    args: IQBoostArgs,
  ): void {
    if (name === ActionEventType.Show || name === ActionEventType.Initialize) {
      if (args.fromKey && args.fromType) {
        this.fromModule.set({ 
          key: args.fromKey,
          type: args.fromType,
        });
      }
      else {
        this.fromModule.set(undefined);
      }

      this.open();
      this.load(
        args.key ?? '',
        args.type ?? 'addressBook',
        args.autoOpen ?? false,
      );
    } else {
      super.doAction(name, args);
    }
  }

  /** Empty key/type means use all entries (broader household context). */
  load(
    key: string = '',
    type: EntryType = 'addressBook',
    autoOpen: boolean = false,
  ): void {
    this.store.reset();

    let loadData = !!key && !!type;

    if (isNullOrEmpty(key) && isNullOrEmpty(type)) {
      loadData = true;
      type = 'addressBook';
      this.store.setUseAllHouseholds(true);
    }

    if (loadData) {
      this.store.setMaximizerEntry(key, type);
      this.store.setLoading(true);

      const fromKey = this.fromModule()?.key;
      const requests = {
        displayValue: this.addressBook.getDisplayValue(key),
        session: this.session.getInfo(),
        opportunityDisplayValue: fromKey 
          ? this.opportunity.getDisplayValue(fromKey)
          : of<string | null>(''),
      };

      this.addSubscription(
        forkJoin(requests)
          .pipe(
            switchMap((response) => 
              this.aiFeaturesService.getConfiguration(response.session)
                .pipe(map(() => response))
            ),
            tap((response) => {
              this.store.setEntryDisplayValue(response.displayValue ?? '');
              this.store.setOpportunityDisplayValue(response.opportunityDisplayValue ?? '');
              this.store.setSessionInfo(response.session);
            }),
            switchMap((response) => 
              this.aiFeaturesService.processFiltersConfiguration(response.session, true)
                .pipe(
                  tap((dataProcessingFeatureGroup) => {
                    this.firstLoad.set(false);

                    if (!dataProcessingFeatureGroup.user.wasFirstTimeOpenedPrivacySecurityCenter) {
                      this.tracker.trackEvent(
                        EventJourney.SecurityCenter, 
                        EventAction.Opened, 
                      );
                    }
                  }),
                  map(() => response)
                )
            ),
            switchMap(() => this.loadPromptCatalog()),
            finalize(() => {
              const autoOpenPrompt = autoOpen && this.store.viewType() === ViewType.Report
                ? this.store.promptCategories().flatMap((category) => category.prompts)[0]
                : undefined;

              if (autoOpenPrompt) {
                // usePrompt raises loading again for the ask it triggers.
                Helper.usePrompt(
                  this.store,
                  autoOpenPrompt,
                  this.store.useAllHouseholds(),
                );
              } else {
                // Covers an empty or failed catalog too — otherwise the spinner never clears.
                this.store.setLoading(false);
              }
            })
          )
          .subscribe(),
      );
    }
  }

  private loadPromptCatalog(): Observable<IqBoostPromptCategory[]> {
    return this.maximizerAi
      .getPrompts({
        language: LanguageHelper.language(),
        type: this.store.useAllHouseholds() ? 'all' : 'single',
      })
      .pipe(
        tap((categories) => this.store.setPromptCategories(categories)),
        catchError(() => {
          // Never leave the previous scope's catalog on display for the new scope.
          this.store.setPromptCategories([]);
          return of([] as IqBoostPromptCategory[]);
        }),
      );
  }

  private askAi(input: AskAiInput): Observable<unknown> {
    const promptName = this.resolvePromptName();
    const entityKey = input.key ?? this.store.key() ?? '';

    if (isNullOrEmpty(promptName) || isNullOrEmpty(entityKey)) {
      return of(undefined);
    }

    const request: GenerateIqBoostRequest = {
      entityKey,
      promptName,
      entityType: input.type ?? this.store.type() ?? 'addressBook',
      language: LanguageHelper.language(),
      conversationId: input.conversationId,
      userPrompt: input.userPrompt,
      // Set by the summary view's Regenerate; every other ask may serve from cache.
      invalidateCache: input.invalidateCache,
    };

    return this.streamAi(request);
  }

  /** Streams tokens; phase events drive the status line until the first content chunk. */
  private streamAi(request: GenerateIqBoostRequest): Observable<unknown> {
    let content = '';
    let usage: OpenAI.CompletionResponse['usage'] | undefined;

    return this.maximizerAi.generateResponse(request).pipe(
      tap((chunk) => {
        if (chunk.phase !== undefined) {
          this.store.setStreamingPhase(chunk.phase);
        }
        content += chunk.content ?? '';
        usage = chunk.usage ?? usage;
      }),
      filter((chunk) => !!chunk.content),
      // One paint per frame; auditTime still flushes the final chunk on complete.
      auditTime(0, animationFrameScheduler),
      tap({
        next: () => this.publishStreamedContent(content),
        complete: () => this.publishStreamedResult(content, usage),
      }),
      catchError(() => {
        // Chunks already painted would otherwise read as a finished answer, saveable to the CRM.
        if (this.store.viewType() === ViewType.Report) {
          this.store.setAiResponse('', ViewType.Report);
          this.store.setCurrentPrompt();
        }
        this.store.setShowErrorMessageDialog(true);
        return of(undefined);
      }),
      finalize(() => this.store.completeStreamingResponse()),
    );
  }

  private publishStreamedContent(content: string): void {
    if (this.store.viewType() === ViewType.Report) {
      this.store.setStreamingResponse(content);
    } else if (this.store.viewType() === ViewType.Chat) {
      this.store.triggerStreamingResponseFromAi(content);
    }
  }

  // Chat commits streamed answers itself; publishing here would duplicate them.
  private publishStreamedResult(
    content: string,
    usage: OpenAI.CompletionResponse['usage'] | undefined,
  ): void {
    if (this.store.viewType() === ViewType.Chat) {
      return;
    }

    // A stream that completes with only phase events is a failure, not an empty answer.
    if (isNullOrEmpty(content)) {
      this.store.setShowErrorMessageDialog(true);
      return;
    }

    this.store.triggerResponseFromAi({
      choices: [{ message: { role: 'assistant', content } }],
      usage: usage ?? { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 },
    });
  }

  /** Falls back to the first prompt when chat runs before any prompt is selected. */
  private resolvePromptName(): string {
    const currentPromptName = this.store.currentPrompt()?.name ?? '';
    if (!isNullOrEmpty(currentPromptName)) {
      return currentPromptName;
    }

    const prompts = this.store.promptCategories().flatMap((category) => category.prompts);
    return prompts[0]?.name ?? '';
  }

  private createAndOpenEntity<T>(
    createEntity: T,
    createService: { create: (entity: T) => Observable<string | null> }
  ): void {
    this.store.setLoading(true);
    this.addSubscription(
      createService.create(createEntity)
        .pipe(
          switchMap((key) => !key 
            ? of(null) 
            : this.timeline.forEvent(key)
          ),
          tap((entity) => entity && this.store.triggerOpenEntity(new OpenEntityArgs(entity))),
          finalize(() => {
            this.store.setLoading(false);
            this.store.setIsOpenConfirmCreateCRMAction(false);
          })
        )
        .subscribe()
    );
  }

  createCRMAction(createCRMAction: IQBoostCreateCRMAction): void {
    const menuOptionName = createCRMAction.action;

    if (menuOptionName) {
      const { startDate, endDate } = Helper.formatDateRange(createCRMAction.date);
      let eventActionType: string | undefined = undefined;
      let parentKey = this.store.key() ?? '';

      if (this.store.useAllHouseholds()) {
        parentKey = createCRMAction.key;
      }

      switch (menuOptionName) {
        case MenuOptionName.AddNote:
          {
            const note = {
              Key: null,
              ParentKey: this.fromModule()?.key ?? parentKey,
              Category: AI_GENERATED_CATEGORY,
              RichText: AiMarkupUtils.removeAllMarkupAndPII(`${createCRMAction.title}\n${createCRMAction.description}`, this.piiAnonymization),
              Type: {
                Key: Octopus.DefaultNotes.MANUAL
              },
              DateTime: new Date(startDate),
            } as Octopus.NoteCreate;

            this.createAndOpenEntity(note, this.note);
            eventActionType = 'create-note';
          }
          break;
        case MenuOptionName.ScheduleToDo:
          {
            const task = {
              Key: null,
              Activity: AiMarkupUtils.removeAllMarkupAndPII(`${createCRMAction.title}\n${createCRMAction.description}`, this.piiAnonymization),
              AbEntryKey: parentKey,
              Datetime: new Date(startDate),
              WithKey: this.fromModule()?.key ?? null,
            } as Octopus.TaskCreate;

            this.createAndOpenEntity(task, this.task);
            eventActionType = 'create-todo';
          }
          break;
        case MenuOptionName.ScheduleMeeting: 
          {
            const appointment = {
              Key: null,
              Subject: AiMarkupUtils.removeAllMarkupAndPII(createCRMAction.title, this.piiAnonymization),
              StartDate: startDate,
              EndDate: endDate,
              Description: AiMarkupUtils.removeAllMarkupAndPII(createCRMAction.description, this.piiAnonymization),
              Organizer: this.store.sessionInfo()?.user?.key ?? '',
              With: this.fromModule()?.key ?? null,
              AbEntries: [{
                Key: {
                  Value: parentKey
                }
              }],
            } as Octopus.AppointmentCreate;

            this.createAndOpenEntity(appointment, this.appointment);
            eventActionType = 'create-meeting';
          }
          break;
      }

      if (eventActionType) {
        this.tracker.trackEvent(
          EventJourney.Prompt, 
          EventAction.Clicked, 
          {
            name: eventActionType,
            prompt: this.store.currentPrompt()?.name ?? '',
          });
      }
    }
  }

  closeConfirmation(confirmed: boolean): void {
    this.isConfirmationDialogOpen.set(false);

    if (confirmed) {
      this.store.setLoading(true);

      const note = {
        DateTime: new Date(),
        ParentKey: this.fromModule()?.key ?? this.store.key(),
        Category: AI_GENERATED_CATEGORY,
        RichText: AiMarkupUtils.removeAllMarkupAndPII(
          this.markdown.transform(this.store.aiResponse().summary),
          this.piiAnonymization,
        ),
        Type: {
          Key: Octopus.DefaultNotes.MANUAL
        },
      } as Octopus.NoteCreate;

      this.addSubscription(
        this.note.create(note)
          .pipe(
            finalize(() => this.store.setLoading(false))
          )
          .subscribe()
      );
    }
  }

  sendSuggestion(): void {
    const caseNumber = uuidV4().substring(0, 8);
    const to = 'pm@maximizer.com';
    const user = this.store.sessionInfo()?.user;
    const subject = this.resourceOrTranslate.transform(
      undefined,
      'iqboost.suggestPrompt.email.subject',
    )
      .replace('{{firstName}}', user?.firstName ?? '')
      .replace('{{lastName}}', user?.lastName ?? '')
      .replace('{{caseNumber}}', caseNumber);
    const userEmail = user?.email1?.email ?? user?.email2?.email ?? user?.email3?.email ?? '';
    const body = this.resourceOrTranslate.transform(
      undefined,
      'iqboost.suggestPrompt.email.body',
    )
      .replace('{{firstName}}', user?.firstName ?? '')
      .replace('{{lastName}}', user?.lastName ?? '')
      .replace('{{email}}', userEmail)
      .replace('{{databaseId}}', (top as any)?.maximizerIdentity?.database_id ?? '')
      .replace('{{caseNumber}}', caseNumber)
      .replace('{{suggestion}}', this.suggestPromptBody());
    const request: EmailMessageRequest = {
      EmailMessage: {
        Data: {
          From: userEmail,
          To: [to],
          Subject: subject,
          Type: 0,
          Body: body,
        },
      },
    };

    this.store.setLoading(true);
    this.emailService.sendSuggestion(request)
      .pipe(
        tap((response) => {
          if (response) {
            this.notificationMessage.set('iqboost.suggestPrompt.notifications.success');
            this.baseNotifications.type = 'success';
            this.baseNotifications.icon = 'circle-check';
            this.baseNotifications.show();
          } else {
            this.notificationMessage.set('iqboost.suggestPrompt.notifications.error');
            this.baseNotifications.type = 'error';
            this.baseNotifications.icon = undefined;
            this.baseNotifications.show();
          }
        }),
        finalize(() => {
          this.isSuggestPromptDialogOpen.set(false);
          this.store.setLoading(false);
        }),
      )
      .subscribe();
  }

  openPrivacySecurityCenter(): void {
    this.store.setViewType(ViewType.PrivacySecurityCenter);
    this.tracker.trackEvent(
      EventJourney.SecurityCenter, 
      EventAction.Opened,
    );
  }

  open(): void {
    this.subscribeToEvents();
    this.context.startDialogMode();
    this.isOpen.set(true);

    this.tracker.trackEvent(
      EventJourney.Action, 
      EventAction.Opened,
    );
  }

  close(): void {
    this.unsubscribeAll();
    this.context.endDialogMode();
    this.isOpen.set(false);
    this.store.setLoading(false);

    this.tracker.trackEvent(
      EventJourney.Action, 
      EventAction.Closed,
    );
  }

  startUserGuide(): void {
    this.tracker.trackEvent(
      EventJourney.Help, 
      EventAction.Clicked,
      {
        name: ViewType[this.store.viewType()],
        prompt: this.store.currentPrompt()?.name ?? '',
      }
    );
  }
}
